'use client'

import { AnimatePresence, MotionProps, motion, useWillChange } from 'motion/react'
import { MouseEventHandler, useCallback, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useKey } from 'react-use'
import { IChildrenProps } from '@/typings/children-props'
import { run } from '@/utils/run'
import { cn } from '@/utils/styles/cn'

export interface ModalProps extends IChildrenProps {
    isOpen: boolean
    onClose?: () => void
    modalMotion?: MotionProps
    overlayMotion?: MotionProps
    modalClassNameStyles?: string
}

export const Modal = ({
    isOpen,
    onClose: propsOnClose,
    children,
    modalMotion,
    overlayMotion,
    modalClassNameStyles,
}: ModalProps) => {
    const contentRef = useRef<(HTMLElement & HTMLDivElement) | null>(null)

    const willChange = useWillChange()

    const scrollYRef = useRef(0)

    const onClose = useCallback(() => {
        if (!isOpen) return

        propsOnClose?.()
    }, [isOpen, propsOnClose])

    useKey(
        'Escape',
        () => {
            if (isOpen) onClose?.()
        },
        {},
        [isOpen]
    )

    useEffect(() => {
        if (!isOpen || typeof window === 'undefined' || scrollYRef.current === window.scrollY)
            return

        scrollYRef.current = window.scrollY
    }, [isOpen])

    const handleOutsideClick: MouseEventHandler<HTMLDivElement> = (event) => {
        event.stopPropagation()
        event.preventDefault()

        onClose?.()
    }

    const modalRoot = typeof document !== 'undefined' && document.getElementById('modal-root')

    if (!modalRoot) return null

    return createPortal(
        <AnimatePresence mode="sync" initial>
            {run(() => {
                if (!isOpen) return null

                return (
                    <motion.div
                        key={'modal' + 'container'}
                        initial={{ backgroundColor: 'rgba(0,0,0,0)' }}
                        animate={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
                        exit={{ opacity: 0, transition: { duration: 0.2 } }}
                        transition={{ duration: 0.15, ease: 'linear' }}
                        onClick={handleOutsideClick}
                        style={{ willChange }}
                        className="bg-white z-modal flex items-center justify-center fixed top-0 left-0 w-full h-full"
                        {...overlayMotion}
                    >
                        <motion.div
                            onClick={(e) => e.stopPropagation()}
                            ref={contentRef}
                            initial={{ opacity: 0, y: 0, scale: 0.5 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{
                                opacity: 0,
                                y: 20,
                                scale: 0.9,
                                transition: { opacity: { duration: 0.15 } },
                            }}
                            transition={{
                                opacity: { duration: 0.3, ease: 'easeOut' },
                                y: { type: 'spring', stiffness: 300, damping: 29 },
                                scale: { type: 'spring', bounce: 0.25, duration: 0.4 },
                            }}
                            style={{ willChange }}
                            key={'modal' + 'children-container'}
                            className={cn(
                                'bg-white rounded-[20px] shadow-custom relative overflow-hidden transform-origin-center w-full',
                                modalClassNameStyles
                            )}
                            {...modalMotion}
                        >
                            {children}
                        </motion.div>
                    </motion.div>
                )
            })}
        </AnimatePresence>,
        modalRoot
    )
}

export const ModalRoot = () => {
    return <div id="modal-root" />
}

export default Modal
